import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useBoolean } from '@sravni/react-utils';

import { sendReviewEvent } from '../../../../../helpers/analyticsEvents';
import { handleDownloadFiles } from '../../../../../helpers/downloadFiles';
import { IFile } from '../../../../../interfaces/files';
import { loadFileInfo } from '../../../../../services/files';
import { useCurrentReviewId } from '../../../../../store/hooks/createReviewData';
import { REVIEW_FIELDS } from '../../../constants/common';
import { EVENT_ACTIONS } from '../../../helpers/analytics';
import { useFieldChanged } from '../../../hooks/useFieldChanged';
import { TAttachments } from '../../../types';
import { UploadFile } from '../../UploadFile';

type TUploadFilesProps = {
  isReadOnly: boolean;
  commonEventLabel: string[];
};

export const UploadFiles: React.FC<TUploadFilesProps> = (props) => {
  const { isReadOnly, commonEventLabel } = props;
  const { control, setValue, getValues } = useFormContext();

  const reviewId = useCurrentReviewId();

  const [existedFiles, setExistedFiles] = useState<IFile[]>([]);
  const [newFilesToAttach, setNewFilesToAttach] = useState<IFile[]>([]);
  const [isExistedFilesLoadedToState, setExistedFilesLoadedToState] = useBoolean(false);
  const [isExistedFilesLoadingToState, setExistedFilesLoadingToState] = useBoolean(false);

  const removeExistedFile = useCallback(
    (fileToDelete: IFile) => {
      setExistedFiles((prev) => prev.filter((file) => file !== fileToDelete));
      sendReviewEvent({
        label: commonEventLabel,
        action: EVENT_ACTIONS.REMOVE_DOCUMENT,
      });
    },
    [commonEventLabel],
  );

  const removeNewFile = useCallback(
    (fileToDelete: IFile) => {
      setNewFilesToAttach((prev) => prev.filter((file) => file !== fileToDelete));
      sendReviewEvent({
        label: commonEventLabel,
        action: EVENT_ACTIONS.REMOVE_DOCUMENT,
      });
    },
    [commonEventLabel],
  );

  const handleUploadFile = useCallback(
    (file: IFile) => {
      setNewFilesToAttach((prev) => [...prev, file]);
      sendReviewEvent({
        label: commonEventLabel,
        action: EVENT_ACTIONS.UPLOAD_DOCUMENT,
      });
    },
    [commonEventLabel],
  );

  const fetchAttachmentInfo = async (fileId: string) => {
    const fileInfo = await loadFileInfo(fileId);
    setExistedFiles((prev) => [...prev, fileInfo]);
  };

  const loadAttachmentsFromReview = async (attachments: TAttachments) => {
    const promises = attachments?.map((fileId) => fetchAttachmentInfo(fileId));

    setExistedFilesLoadingToState.on();

    await Promise.all(promises);

    setExistedFilesLoadedToState.on();
    setExistedFilesLoadingToState.off();
  };

  const onChangeAttachments = ({ value: attachments }) => {
    if (isExistedFilesLoadedToState || isExistedFilesLoadingToState) return;

    attachments && loadAttachmentsFromReview(attachments);
  };

  useFieldChanged({
    name: REVIEW_FIELDS.ATTACHMENTS,
    control,
    getValues,
    onChange: onChangeAttachments,
  });

  useEffect(() => {
    if (!isExistedFilesLoadedToState || isExistedFilesLoadingToState) return;

    const newValue = [...existedFiles, ...newFilesToAttach].map((attachment) => attachment.id);

    setValue(REVIEW_FIELDS.ATTACHMENTS, newValue, { shouldDirty: true, shouldTouch: true });
  }, [newFilesToAttach, existedFiles, isExistedFilesLoadedToState, isExistedFilesLoadingToState, setValue]);

  const onDownloadFiles = useCallback(
    async (attachmentIds: string[]) => {
      if (!reviewId) return;

      await handleDownloadFiles(reviewId)(attachmentIds);
    },
    [reviewId],
  );

  return (
    <UploadFile
      isReadOnly={isReadOnly}
      onDownloadFiles={onDownloadFiles}
      handleUploadFile={handleUploadFile}
      removeExistedFile={removeExistedFile}
      removeNewFile={removeNewFile}
      existedFiles={existedFiles}
    />
  );
};
